import { Alert, Checkbox, Modal, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import { projectMemberRoleNames } from '../../api/projects';
import {
  applyProjectTemplate,
  getProjectTemplate,
  listProjectTemplates,
} from '../../api/projectTemplates';
import type { ProjectTemplate, ProjectTemplateTask } from '../../api/projectTemplates';

/** Tham số của cửa sổ áp mẫu. */
interface ApplyProjectTemplateModalProperties {
  readonly open: boolean;
  readonly projectId: string;

  /** Loại dự án — chỉ những mẫu cùng loại mới áp được. */
  readonly projectTypeId: string;

  /** Đóng cửa sổ mà không làm gì. */
  readonly onClose: () => void;

  /** Áp mẫu xong — nơi gọi tải lại cây công việc. */
  readonly onApplied: () => void;
}

/** Một dòng xem trước: việc trong mẫu kèm độ sâu. */
interface PreviewRow {
  readonly task: ProjectTemplateTask;
  readonly depth: number;
}

/** Bề rộng một cấp thụt lề, tính bằng điểm ảnh. */
const indentWidth = 18;

/**
 * Xếp cây công việc của mẫu thành danh sách phẳng theo thứ tự đọc, kèm độ sâu.
 *
 * Bản xem trước phải hiện đúng hình dạng cây mà mẫu sẽ sinh ra. Hiện phẳng theo số thứ tự sẽ làm
 * người lập kế hoạch không thấy được việc nào là việc con của việc nào — và đó chính là thứ họ cần
 * kiểm trước khi bấm.
 */
function flatten(tasks: readonly ProjectTemplateTask[]): readonly PreviewRow[] {
  const childrenByParent = new Map<string | null, ProjectTemplateTask[]>();

  for (const task of tasks) {
    const siblings = childrenByParent.get(task.parentId);

    if (siblings === undefined) {
      childrenByParent.set(task.parentId, [task]);
    } else {
      siblings.push(task);
    }
  }

  for (const siblings of childrenByParent.values()) {
    siblings.sort((left, right) => left.sequenceNumber - right.sequenceNumber);
  }

  const rows: PreviewRow[] = [];
  const visited = new Set<string>();

  function visit(parentId: string | null, depth: number): void {
    for (const task of childrenByParent.get(parentId) ?? []) {
      if (visited.has(task.id)) {
        continue;
      }

      visited.add(task.id);
      rows.push({ task, depth });
      visit(task.id, depth + 1);
    }
  }

  visit(null, 0);

  for (const task of tasks) {
    if (!visited.has(task.id)) {
      rows.push({ task, depth: 0 });
    }
  }

  return rows;
}

/**
 * Tập số thứ tự bị bỏ, tính lan xuống cả nhánh dưới.
 *
 * Bỏ một nhóm việc mà giữ lại việc con của nó thì cây sinh ra sẽ có việc con treo lơ lửng không có
 * cha. Máy chủ cũng lan xuống nhánh dưới đúng như vậy — chỗ này tính lại để bản xem trước hiện đúng
 * những gì sẽ xảy ra, chứ không phải để thay máy chủ.
 */
function expandExcluded(
  tasks: readonly ProjectTemplateTask[],
  excludedIds: ReadonlySet<string>,
): readonly number[] {
  const byId = new Map(tasks.map((task) => [task.id, task]));
  const excluded = new Set<number>();

  for (const task of tasks) {
    let current: ProjectTemplateTask | undefined = task;

    while (current !== undefined) {
      if (excludedIds.has(current.id)) {
        excluded.add(task.sequenceNumber);
        break;
      }

      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
  }

  return [...excluded];
}

/**
 * Cửa sổ áp một mẫu vào dự án.
 *
 * <b>Có bản xem trước, và đó là phần quan trọng nhất.</b> Áp mẫu sinh ra hàng chục dòng trong một
 * lần bấm, và chỉ áp được một lần cho mỗi dự án — nên người lập kế hoạch phải thấy trước mình sắp
 * nhận được gì. Không có xem trước thì cách duy nhất để biết là bấm rồi đọc kết quả, và lúc đó thì
 * đã muộn.
 *
 * <b>Chỉ bỏ chọn được việc đánh dấu tùy chọn.</b> Ô tích của việc bắt buộc bị vô hiệu hóa kèm lời
 * giải thích, chứ không ẩn đi: người dùng cần biết vì sao mình không bỏ được, và câu trả lời là bỏ
 * một việc bắt buộc nghĩa là mẫu này không đúng cho dự án đó.
 */
export function ApplyProjectTemplateModal({
  open,
  projectId,
  projectTypeId,
  onClose,
  onApplied,
}: ApplyProjectTemplateModalProperties): ReactNode {
  const [templates, setTemplates] = useState<readonly ProjectTemplate[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /*
    Cây công việc đã nạp được giữ KÈM định danh mẫu mà nó thuộc về, thay vì xóa trắng mỗi lần người
    dùng đổi mẫu. Xóa trắng phải làm ngay trong thân hiệu ứng, và đặt trạng thái ngay trong thân hiệu
    ứng gây ra một vòng vẽ lại dây chuyền. Suy ra từ định danh đang chọn thì không cần vòng nào.
  */
  const [loadedTasks, setLoadedTasks] = useState<{
    readonly templateId: string;
    readonly tasks: readonly ProjectTemplateTask[];
  } | null>(null);
  const [excludedIds, setExcludedIds] = useState<ReadonlySet<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        /*
          Chỉ lấy mẫu CÒN DÙNG và CÙNG LOẠI dự án. Hiện cả mẫu đã ngừng dùng rồi để máy chủ từ chối
          là bắt người dùng bấm mới biết — trong khi giao diện đã biết trước.
        */
        const loaded = await listProjectTemplates({ projectTypeId, onlyActive: true });

        if (!isCurrent) {
          return;
        }

        setTemplates(loaded);

        // Mẫu mặc định của loại dự án này được chọn sẵn: với phần lớn dự án thì đó là mẫu đúng.
        const preferred = loaded.find((template) => template.isDefault) ?? null;

        if (preferred !== null) {
          setSelectedId(preferred.id);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách mẫu.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [open, projectTypeId]);

  useEffect(() => {
    if (!open || selectedId === null) {
      return;
    }

    let isCurrent = true;

    async function load(identifier: string): Promise<void> {
      setErrorMessage(null);
      setExcludedIds(new Set());

      try {
        const detail = await getProjectTemplate(identifier);

        if (!isCurrent) {
          return;
        }

        setLoadedTasks({ templateId: identifier, tasks: detail.tasks });
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được cây công việc của mẫu.',
        );
      }
    }

    void load(selectedId);

    return () => {
      isCurrent = false;
    };
  }, [open, selectedId]);

  const toggleExcluded = useCallback((taskId: string) => {
    setExcludedIds((current) => {
      const next = new Set(current);

      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }

      return next;
    });
  }, []);

  async function apply(): Promise<void> {
    if (selectedId === null || tasks === null) {
      return;
    }

    setIsApplying(true);

    try {
      const result = await applyProjectTemplate(projectId, {
        projectTemplateId: selectedId,
        excludedSequenceNumbers: expandExcluded(tasks, excludedIds),
      });

      message.success(
        `Đã sinh ${String(result.createdTaskCount)} việc, `
          + `${String(result.createdChecklistItemCount)} mục kiểm và `
          + `${String(result.createdDependencyCount)} quan hệ phụ thuộc.`
          + (result.skippedOptionalCount === 0
            ? ''
            : ` Bỏ ${String(result.skippedOptionalCount)} việc tùy chọn.`),
      );

      onApplied();
      onClose();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không áp được mẫu.');
    } finally {
      setIsApplying(false);
    }
  }

  const tasks = loadedTasks?.templateId === selectedId ? loadedTasks.tasks : null;

  const rows = tasks === null ? [] : flatten(tasks);

  const excludedSequenceNumbers = new Set<number>(
    tasks === null ? [] : expandExcluded(tasks, excludedIds),
  );

  const columns: TableColumnsType<PreviewRow> = [
    {
      title: 'Sinh ra',
      key: 'included',
      width: 90,
      align: 'center',
      render: (_: unknown, row) => {
        const isExcluded = excludedSequenceNumbers.has(row.task.sequenceNumber);

        return (
          <Checkbox
            checked={!isExcluded}
            disabled={!row.task.isOptional}
            title={
              row.task.isOptional
                ? 'Bỏ chọn thì việc này không được sinh ra.'
                : 'Việc bắt buộc của quy trình — không bỏ được. Nếu không cần thì chọn mẫu khác.'
            }
            onChange={() => {
              toggleExcluded(row.task.id);
            }}
          />
        );
      },
    },
    {
      title: 'STT',
      key: 'sequenceNumber',
      width: 70,
      align: 'right',
      render: (_: unknown, row) => <Typography.Text code>{row.task.sequenceNumber}</Typography.Text>,
    },
    {
      title: 'Tên việc',
      key: 'name',
      render: (_: unknown, row) => {
        const isExcluded = excludedSequenceNumbers.has(row.task.sequenceNumber);

        return (
          <div style={{ paddingLeft: row.depth * indentWidth }}>
            <Space size={4} wrap>
              {isExcluded ? (
                <Typography.Text delete type="secondary">
                  {row.task.name}
                </Typography.Text>
              ) : (
                <Typography.Text>{row.task.name}</Typography.Text>
              )}
              {row.task.isMilestone ? <Tag color="gold">Mốc</Tag> : null}
              {row.task.isOptional ? <Tag color="blue">Tùy chọn</Tag> : null}
              {row.task.defaultChecklistItems.length === 0 ? null : (
                <Tag color="green">{row.task.defaultChecklistItems.length} mục kiểm</Tag>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Vai trò dự kiến',
      key: 'defaultAssigneeRole',
      width: 170,
      render: (_: unknown, row) =>
        row.task.defaultAssigneeRole === null
          ? '—'
          : projectMemberRoleNames[row.task.defaultAssigneeRole],
    },
    {
      title: 'Chờ việc',
      key: 'dependsOn',
      width: 120,
      render: (_: unknown, row) =>
        row.task.dependsOnSequenceNumbers.length === 0 ? (
          '—'
        ) : (
          <Space size={2} wrap>
            {row.task.dependsOnSequenceNumbers.map((sequenceNumber) => (
              <Tag key={sequenceNumber}>{sequenceNumber}</Tag>
            ))}
          </Space>
        ),
    },
  ];

  const willCreateCount = rows.length - excludedSequenceNumbers.size;

  return (
    <Modal
      open={open}
      width={900}
      title="Áp mẫu dự án"
      okText={`Áp mẫu — sinh ${String(willCreateCount)} việc`}
      cancelText="Đóng"
      okButtonProps={{ disabled: selectedId === null || willCreateCount === 0 }}
      confirmLoading={isApplying}
      onCancel={onClose}
      onOk={() => {
        void apply();
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="Chỉ áp được một lần"
          description="Áp mẫu lần thứ hai sẽ nhân đôi cả cây công việc, nên hệ thống chỉ cho áp khi dự án chưa có việc nào. Hãy xem kỹ bản dưới đây trước khi bấm."
        />

        {errorMessage === null ? null : <Alert type="error" showIcon message={errorMessage} />}

        {templates === null ? (
          <Spin />
        ) : (
          <Select<string | null>
            style={{ width: '100%' }}
            placeholder="Chọn mẫu"
            value={selectedId}
            onChange={(value) => {
              setSelectedId(value ?? null);
            }}
            options={templates.map((template) => ({
              value: template.id,
              label:
                `${template.code} — ${template.name} (v${String(template.versionNumber)}, `
                + `${String(template.taskCount)} việc)`
                + (template.isDefault ? ' · mặc định' : ''),
              disabled: template.taskCount === 0,
            }))}
            notFoundContent="Loại dự án này chưa có mẫu nào còn dùng."
          />
        )}

        {templates !== null && templates.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="Loại dự án này chưa có mẫu"
            description="Khai một mẫu ở trang Mẫu dự án trước, rồi quay lại đây."
          />
        ) : null}

        {selectedId === null ? null : (
          <Table<PreviewRow>
            rowKey={(row) => row.task.id}
            size="small"
            loading={tasks === null}
            pagination={false}
            columns={columns}
            dataSource={[...rows]}
          />
        )}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Áp mẫu không đặt ngày tháng và không giao việc cho ai. Rải lịch cho đúng thì phải đi theo
          quan hệ phụ thuộc, còn đặt đại một ngày cho mọi việc sẽ sinh ra hàng chục mốc trông như kế
          hoạch mà không phải kế hoạch.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
